"use client"

/**
 * Main App Component for EnerMetis Power Grid
 * This file serves as the entry point of the application and sets up:
 * 1. Navigation
 * 2. Authentication
 * 3. Theming (Dark/Light mode)
 * 4. Screen routing
 *
 *
 * The app starts at the App component, which sets up:
 * 1. Authentication (AuthProvider)
 * 2. Theming (ThemeProvider)
 * 3. Navigation (ThemedApp)
 * 4. Navigation is handled based on authentication:
 *    Not logged in → Shows Login screen
 *    Logged in but no API endpoint → Shows API Config screen
 *    Logged in with API endpoint → Shows Dashboard and other screens
 * 5. Themes automatically adjust between light and dark mode
 */

import { NavigationContainer } from "@react-navigation/native"
import { createNativeStackNavigator } from "@react-navigation/native-stack"
import { Provider as PaperProvider } from "react-native-paper"
import { View, ActivityIndicator, Platform, StyleSheet } from "react-native"
import type { DefaultTheme } from "@react-navigation/native"

// Import authentication and theme providers
import { AuthProvider, useAuth } from "./src/contexts/AuthContext"
import { ThemeProvider, useTheme } from "./src/contexts/ThemeContext"
import { UnitsProvider } from "./src/contexts/UnitsContext"
import { ApiProvider } from "./src/contexts/ApiContext" // Import the new ApiProvider

// Import screens
import { LoginScreen } from "./src/screens/LoginScreen"
import { ApiConfigScreen } from "./src/screens/ApiConfigScreen" // Import the new API config screen
import { AllProjectsDashboard } from "./src/components/ProjectsOverviewDashboard/AllProjectsDashboard"
import ProjectOptionsScreen from "./src/screens/ProjectOptionsScreen"
import ProjectView from "./src/screens/ProjectView"
import ProjectViewNested from "./src/screens/ProjectViewNested"

// Define the type of parameters that can be passed to each screen
export type RootStackParamList = {
  Login: undefined // No parameters needed for login
  ApiConfig: undefined // No parameters needed for API config
  AllProjectsDashboard: undefined // No parameters needed for dashboard
  ProjectOptions: {
    project: any
    onEdit: () => void
    onDelete: () => void
  }
  ProjectView: {
    projectId: string
  }
  ProjectViewNested: {
    projectId: string
  }
}

// Create a navigator that will manage the navigation stack
const Stack = createNativeStackNavigator<RootStackParamList>()

// Theme definitions for dark and light modes
const DarkTheme: typeof DefaultTheme = {
  dark: true,
  colors: {
    primary: "#90caf9", // Light blue for dark theme
    background: "#121212", // Dark background
    card: "#1e1e1e", // Slightly lighter than background
    text: "#ffffff", // White text
    border: "#2c2c2c", // Dark borders
    notification: "#ff5252", // Red notifications
  },
  fonts: {
    regular: {
      fontFamily: "System",
      fontWeight: "400",
    },
    medium: {
      fontFamily: "System",
      fontWeight: "500",
    },
    bold: {
      fontFamily: "System",
      fontWeight: "700",
    },
    heavy: {
      fontFamily: "System",
      fontWeight: "900",
    },
  },
}

const LightTheme: typeof DefaultTheme = {
  dark: false,
  colors: {
    primary: "#1976d2", // Material Blue
    background: "#ffffff", // White background
    card: "#ffffff", // White cards
    text: "#000000", // Black text
    border: "#d0d0d0", // Light gray borders
    notification: "#ff0000", // Red notifications
  },
  fonts: {
    regular: {
      fontFamily: "System",
      fontWeight: "400",
    },
    medium: {
      fontFamily: "System",
      fontWeight: "500",
    },
    bold: {
      fontFamily: "System",
      fontWeight: "700",
    },
    heavy: {
      fontFamily: "System",
      fontWeight: "900",
    },
  },
}

/**
 * Navigation Component
 * Handles the app's navigation structure and authentication state
 */
function Navigation() {
  // Get authentication state and loading status
  const { user, loading, apiEndpointConfigured } = useAuth()
  // Get current theme settings
  const { theme, isDarkMode } = useTheme()

  // Choose theme based on dark/light mode
  const navigationTheme = isDarkMode ? DarkTheme : LightTheme

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    )
  }

  return (
    <NavigationContainer theme={navigationTheme}>
      <Stack.Navigator
        screenOptions={{
          headerShown: false,
          animation: Platform.OS === "ios" ? "default" : "none",
          headerStyle: {
            backgroundColor: navigationTheme.colors.card,
          },
          headerTintColor: navigationTheme.colors.text,
        }}
      >
        {/* Show either Login screen or main app screens based on authentication */}
        {!user ? (
          <Stack.Screen name="Login" component={LoginScreen} />
        ) : !apiEndpointConfigured ? (
          // If user is logged in but API endpoint is not configured, show the API config screen
          <Stack.Screen name="ApiConfig" component={ApiConfigScreen} />
        ) : (
          <>
            {/* Main app screens */}
            <Stack.Screen name="AllProjectsDashboard" component={AllProjectsDashboard} />
            <Stack.Screen
              name="ProjectOptions"
              component={ProjectOptionsScreen}
              options={{
                headerShown: true,
                title: "Project Options",
                presentation: "modal",
              }}
            />
            <Stack.Screen
              name="ProjectView"
              component={ProjectView}
              options={{
                headerShown: true,
                title: "Project View",
              }}
            />
            <Stack.Screen
              name="ProjectViewNested"
              component={ProjectViewNested}
            // options={{
            //   headerShown: false,
            //   title: "Project View (Nested)",
            // }}
            />
          </>
        )}
      </Stack.Navigator>
    </NavigationContainer>
  )
}

/**
 * ThemedApp Component
 * Wraps the Navigation with Paper theme provider for consistent styling
 */
function ThemedApp() {
  const { theme } = useTheme()
  return (
    <PaperProvider theme={theme}>
      <ApiProvider>
        <Navigation />
      </ApiProvider>
    </PaperProvider>
  )
}

/**
 * Main App Component
 * Sets up the provider hierarchy:
 * 1. AuthProvider for authentication
 * 2. ThemeProvider for theming
 * 3. ApiProvider for API URL management
 * 4. UnitsProvider for unit conversions
 * 5. ThemedApp for the actual application
 */
export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <UnitsProvider>
          <ThemedApp />
        </UnitsProvider>
      </ThemeProvider>
    </AuthProvider>
  )
}

// Styles for the components
const styles = StyleSheet.create({
  container: {
    flex: 1, // Take up all available space
  },
  gradient: {
    flex: 1,
    width: "100%",
  },
  content: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 10,
  },
  themeToggle: {
    marginLeft: 10,
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    textAlign: "center",
    textShadowColor: "rgba(0, 0, 0, 0.3)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  subtitle: {
    fontSize: 18,
    textAlign: "center",
    opacity: 0.9,
    marginBottom: 30,
    textShadowColor: "rgba(0, 0, 0, 0.2)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  loginContainer: {
    padding: 20,
    borderRadius: 10,
    width: "100%",
    maxWidth: 400,
    elevation: 4,
  },
  input: {
    marginBottom: 12,
  },
  loginButton: {
    marginTop: 16,
    paddingVertical: 6,
  },
  forgotButton: {
    marginTop: 8,
  },
})

